/**
 * LoginScreen — mock authentication UI
 * Shows a login form + a demo users panel grouped by role
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import * as api from './api';

const ROLE_COLORS = {
  mother:       '#e91e63',
  nurse:        '#0288d1',
  hospital:     '#7b1fa2',
  shop_owner:   '#f57c00',
  kit_admin:    '#388e3c',
  management:   '#1565c0',
  admin:        '#c62828',
  system_admin: '#4e342e',
};

export default function LoginScreen() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [demoUsers, setDemoUsers] = useState([]);
  const [showDemo, setShowDemo]   = useState(false);

  useEffect(() => {
    api.getDemoUsers().then(setDemoUsers).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const quickLogin = async (u, p) => {
    setUsername(u); setPassword(p); setError(''); setLoading(true);
    try { await login(u, p); } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // Group demo users by role
  const grouped = demoUsers.reduce((acc, u) => {
    if (!acc[u.role]) acc[u.role] = [];
    acc[u.role].push(u);
    return acc;
  }, {});

  const isDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const bg     = isDark ? '#1a1208' : '#fdf6f0';
  const surf   = isDark ? '#261a0e' : '#ffffff';
  const text   = isDark ? '#f5e6d3' : '#2d1f14';
  const muted  = isDark ? '#c4956a' : '#7a5c44';
  const border = isDark ? '#3d2c1a' : '#f0d9c8';
  const accent = '#0d7377';

  return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: '"Nunito", system-ui, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🌱</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: accent }}>NutriSakti</div>
          <div style={{ fontSize: 13, color: muted, marginTop: 4 }}>Platform Kesehatan Ibu & Anak v4</div>
        </div>

        {/* Login card */}
        <div style={{ background: surf, borderRadius: 16, padding: 28, border: `1px solid ${border}`, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: text, marginBottom: 20 }}>Masuk ke Akun Anda</div>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: muted, display: 'block', marginBottom: 5 }}>Username</label>
              <input
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Masukkan username"
                autoComplete="username"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${border}`, background: bg, color: text, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: muted, display: 'block', marginBottom: 5 }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Masukkan password"
                autoComplete="current-password"
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${border}`, background: bg, color: text, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {error && (
              <div style={{ background: '#fdecea', color: '#c62828', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 14, border: '1px solid #ef9a9a' }}>
                ❌ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !username || !password}
              style={{ width: '100%', padding: '12px', background: accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', opacity: (!username || !password) ? 0.6 : 1 }}
            >
              {loading ? '⏳ Masuk...' : '🔐 Masuk'}
            </button>
          </form>
        </div>

        {/* Demo users panel */}
        <div style={{ background: surf, borderRadius: 16, border: `1px solid ${border}`, overflow: 'hidden' }}>
          <button
            onClick={() => setShowDemo(v => !v)}
            style={{ width: '100%', padding: '14px 20px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: text, fontSize: 14, fontWeight: 600 }}
          >
            <span>�� Demo Akun (klik untuk login cepat)</span>
            <span style={{ fontSize: 12, color: muted }}>{showDemo ? '▲ Tutup' : '▼ Lihat'}</span>
          </button>

          {showDemo && (
            <div style={{ padding: '0 16px 16px', maxHeight: 400, overflowY: 'auto' }}>
              {Object.entries(grouped).map(([role, users]) => (
                <div key={role} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: ROLE_COLORS[role] || muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, marginTop: 8 }}>
                    {users[0]?.roleMeta?.label || role}
                  </div>
                  {users.map(u => (
                    <button
                      key={u.username}
                      onClick={() => quickLogin(u.username, u.password)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '8px 10px', marginBottom: 4,
                        background: bg, border: `1px solid ${border}`,
                        borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <span style={{ fontSize: 20 }}>{u.avatar}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: text }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: muted }}>
                          <span style={{ fontFamily: 'monospace' }}>{u.username}</span>
                          {' / '}
                          <span style={{ fontFamily: 'monospace' }}>{u.password}</span>
                        </div>
                      </div>
                      <span style={{ fontSize: 10, background: ROLE_COLORS[role] || muted, color: '#fff', padding: '2px 6px', borderRadius: 6, flexShrink: 0 }}>
                        {u.roleMeta?.label || role}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
